import { Button, Heading, Icon, Link as CLink, List, Stack, Text } from '@chakra-ui/react'
import { LuArrowRight, LuBadgeCheck, LuCalendarCheck, LuClock, LuMail, LuMapPin, LuUserCheck } from 'react-icons/lu'
import { Link as RouterLink, Navigate, useLocation, useOutletContext } from 'react-router'
import { useRegistration } from '~/api/hooks'
import { TestProviderBanner } from '~/biometrics/TestProviderBanner'
import { IconTile, type DecoHue } from '~/components/IconTile'
import { ActionBar } from '~/components/member/ActionBar'
import { STEP_LOOK } from '~/components/member/FlowStepper'
import { MemberLayout } from '~/components/member/layout'
import { SkyScene, SplitCard } from '~/components/member/SplitCard'
import { WhyLink } from '~/components/member/WhyLink'
import { Rich, useT } from '~/i18n/use-t'
import { pathForStatus } from '~/lib/flow'
import { ROOMY } from '~/theme/media'
import { useFlowSteps } from '~/lib/steps'
import type { JoinContext } from './JoinRoute'

const CHECKS = [
  ['check1', LuUserCheck, 'green'],
  ['check2', LuBadgeCheck, 'blue'],
  ['check3', LuCalendarCheck, 'peach'],
  ['check4', LuMapPin, 'green'],
] as const satisfies ReadonlyArray<readonly [string, unknown, DecoHue]>

/** The showcase beside the card on wide screens (after the vocdoni-app sign-in): the sky with the dove and the 5 steps. */
function Showcase() {
  const { t } = useT()
  const { steps, total } = useFlowSteps()
  return (
    <SkyScene data-showcase doveWidth={190}>
      <Stack position='relative' gap={4} px={10} pb={10} mt='auto' color='fg'>
        <Text color='fg.muted'>{t('welcome.theSteps', { n: total })}</Text>
        <List.Root as='ol' gap={3} listStyleType='none' m={0} p={0}>
          {steps.map((key) => (
            <List.Item key={key} display='flex' alignItems='center' gap={3}>
              <IconTile icon={STEP_LOOK[key].icon} hue={STEP_LOOK[key].hue} size='sm' />
              <Text as='span'>{t(`welcome.step_${key}`)}</Text>
            </List.Item>
          ))}
        </List.Root>
      </Stack>
    </SkyScene>
  )
}

/**
 * S0 Welcome (app flow, spec §3): a split card after the vocdoni-app sign-in. Left: the title, the four checks, the age
 * line, the own-email line with its inline "Why?", the duration and the FAQ link on one line; Start sits in the action bar. What used to
 * be folded ("What happens", the steps) is in the FAQ. ("Already registered? Log in" is the header's button.) Right
 * (wide screens only): the sky with the dove and the steps.
 */
export default function Welcome() {
  const { t } = useT()
  const { join } = useOutletContext<JoinContext>()
  // F15: the owner sets recentDevicesOnly on the AWS settings after the device test (spec E10.9).
  const recentOnly = join.publicSettings.recentDevicesOnly === true
  // A registration under way (the cookie) is resumed, never restarted by accident; a 401 here just means "none".
  const reg = useRegistration()
  const inProgress = reg.data && reg.data.status !== 'erased'
  const resumeTo = inProgress ? pathForStatus(reg.data) : null
  const ended = (useLocation().state as { ended?: boolean } | null)?.ended === true
  // A finished registration (approved or review) still holding its cookie: the status page turns it into the voter
  // session and opens the voter page (passwordless spec §2), with no sign-in.
  if (reg.data?.claimable && resumeTo) return <Navigate to={resumeTo} replace />
  return (
    <MemberLayout wide>
      <SplitCard showcase={<Showcase />}>
        {/* T23: a test provider's link shows the staff-only banner above the title (copy v8, E12.9). */}
        <TestProviderBanner join={join} />
        <Heading
          as='h1'
          textWrap='balance'
          css={{ fontSize: '2xl', lineHeight: '1.25', [ROOMY]: { fontSize: '1.75rem' } }}
        >
          {t('welcome.title')}
        </Heading>
        <Stack gap={3}>
          <Text color='fg.muted'>{t('welcome.intro')}</Text>
          {/* Two columns on every width: two short lines per cell on the smallest phone still beat four rows. */}
          <List.Root
            listStyleType='none'
            m={0}
            p={0}
            display='grid'
            gridTemplateColumns='repeat(2, minmax(0, 1fr))'
            rowGap={2}
            columnGap={2}
            alignItems='start'
          >
            {CHECKS.map(([k, icon, hue]) => (
              <List.Item key={k} display='flex' alignItems='center' gap={2} minW={0}>
                <IconTile icon={icon} hue={hue} size='sm' flexShrink={0} />
                <Text as='span' minW={0}>
                  {t(`welcome.${k}`)}
                </Text>
              </List.Item>
            ))}
          </List.Root>
          {/* The human-review line (Art. 22): never folded away. */}
          <Text color='fg.muted'>{t('welcome.ageNote')}</Text>
        </Stack>
        {/* The only warning before the silent "already a member" email (U3): never folded away. */}
        <Text fontWeight='medium'>
          <Icon as={LuMail} aria-hidden color='fg.muted' verticalAlign='-0.125em' me={2} />
          {t('welcome.ownEmail')} <WhyLink faq='ownEmail' inline />
        </Text>
        {ended && <Text role='status'>{t('welcome.sessionEnded')}</Text>}
        <Text color='fg.muted'>
          <Icon as={LuClock} aria-hidden verticalAlign='-0.125em' me={1.5} />
          <Rich k='welcome.duration' />
          <Text as='span' aria-hidden mx={2}>
            &middot;
          </Text>
          <WhyLink label={t('welcome.faqLink')} inline />
        </Text>
        {resumeTo && (
          <CLink
            asChild
            minH='12'
            display='inline-flex'
            alignItems='center'
            alignSelf='flex-start'
            textDecoration='underline'
          >
            <RouterLink to={`/join/${join.providerId}/about`}>{t('welcome.startAgain')}</RouterLink>
          </CLink>
        )}
        {recentOnly && <Text color='fg.muted'>{t('welcome.recentDevices')}</Text>}
        <ActionBar>
          <Button asChild size='lg' w='full'>
            <RouterLink to={resumeTo ?? `/join/${join.providerId}/about`}>
              {resumeTo ? t('welcome.resume') : t('welcome.start')} <LuArrowRight aria-hidden />
            </RouterLink>
          </Button>
        </ActionBar>
      </SplitCard>
    </MemberLayout>
  )
}
